import { AfterViewInit, Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { DynamicApiService, DynamicForm } from '../dynamic/dynamic-api.service';
import {
  ProcessDefinitionDetail,
  ProcessVersionCanonicalGraph,
  ProcessVersionDetail,
  WorkflowApiService
} from './workflow-api.service';
import { WorkflowOutgoingFlowOption } from './workflow-catalog.models';
import { loadBpmnJs, loadBpmnStyles } from './bpmn-js-loader';
import { isInteractiveOperatorType, isServiceOperatorType } from './workflow-operator-config/workflow-operator-config.utils';
import { WorkflowServiceOperatorPanelComponent } from './workflow-operator-config/workflow-service-operator-panel.component';
import { WorkflowApiCallOperatorPanelComponent } from './workflow-operator-config/workflow-api-call-operator-panel.component';
import { WorkflowAiOperatorPanelComponent } from './workflow-operator-config/workflow-ai-operator-panel.component';
import { WorkflowTriggerConfigPanelComponent } from './workflow-operator-config/workflow-trigger-config-panel.component';
import { WorkflowActionRoutingPanelComponent } from './workflow-operator-config/workflow-action-routing-panel.component';
import { WorkflowModuleSurfaceOperatorPanelComponent } from './workflow-operator-config/workflow-module-surface-operator-panel.component';
import {
  finalizeOperatorConfiguration,
  validateModuleSurfaceOperatorConfiguration
} from './workflow-operator-config/workflow-module-surface.designer.utils';
import { WorkflowModuleSurfaceRegistry } from '../../shared/workflow-surfaces/workflow-module-surface.registry';
import { PartySelectorComponent } from '../../shared/design-system/party-selector.component';
import { PLATFORM_PARTY_KIND_OPTIONS, partyKindToApiValue, parsePartyKind } from '../../shared/design-system/party-kind-options';
import { SelectModule } from 'primeng/select';

const DEFAULT_BPMN_XML = `<?xml version="1.0" encoding="UTF-8"?>
<bpmn:definitions xmlns:bpmn="http://www.omg.org/spec/BPMN/20100524/MODEL"
                  xmlns:bpmndi="http://www.omg.org/spec/BPMN/20100524/DI"
                  xmlns:dc="http://www.omg.org/spec/DD/20100524/DC"
                  xmlns:di="http://www.omg.org/spec/DD/20100524/DI"
                  id="Definitions_Default"
                  targetNamespace="http://ain.local/workflow">
  <bpmn:process id="Process_Default" isExecutable="true">
    <bpmn:startEvent id="startEvent_1" name="Start" />
    <bpmn:userTask id="task_approval" name="Approval Task" />
    <bpmn:endEvent id="endEvent_1" name="End" />
    <bpmn:sequenceFlow id="flow_start_to_task" sourceRef="startEvent_1" targetRef="task_approval" />
    <bpmn:sequenceFlow id="flow_task_to_end" sourceRef="task_approval" targetRef="endEvent_1" />
  </bpmn:process>
</bpmn:definitions>`;

const DEFAULT_TRIGGER: OrchestratorStartTrigger = {
  id: 'init-trigger',
  name: 'Manual Start',
  type: 'Manual',
  configurationJson: '{}'
};

const DEFAULT_OPERATOR: OrchestratorOperator = {
  id: 'init-op',
  name: 'Approval Task',
  type: 'UserTask',
  bpmnElementId: 'task_approval',
  description: '',
  referenceType: 'none',
  referenceId: null,
  assigneeType: null,
  assigneeId: null,
  dueDays: 0,
  dueHours: 0,
  dueMinutes: 0,
  metadataJson: '{}',
  configurationJson: '{}'
};


@Component({
  standalone: true,
  selector: 'ain-workflow-definition-detail',
  imports: [
    AinPageComponent,
    AinStateComponent,
    ButtonModule,
    DialogModule,
    InputTextModule,
    TextareaModule,
    FormsModule,
    RouterLink,
    WorkflowServiceOperatorPanelComponent,
    WorkflowApiCallOperatorPanelComponent,
    WorkflowAiOperatorPanelComponent,
    WorkflowTriggerConfigPanelComponent,
    WorkflowActionRoutingPanelComponent,
    WorkflowModuleSurfaceOperatorPanelComponent,
    PartySelectorComponent,
    SelectModule
  ],
  template: `
    <ain-page [title]="definition()?.name ?? t('workflow.definitions.title')" [description]="t('workflow.definitions.detail')">
      <a routerLink="/workflow/definitions">{{ t('common.back') }}</a>
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="error() ?? ''" />
      } @else if (definition(); as def) {
        <div class="actions">
          <button pButton type="button" severity="secondary" [label]="t('common.save')" (click)="saveDefinition()"></button>
          <button pButton type="button" [label]="t('workflow.publish')" (click)="publish()"></button>
          <button pButton type="button" severity="secondary" [label]="t('workflow.startInstance')" (click)="startInstance()"></button>
          <button pButton type="button" severity="danger" [label]="t('workflow.archive')" (click)="archive()"></button>
        </div>

        <div class="detail-tabs">
          <button
            type="button"
            pButton
            severity="secondary"
            [outlined]="activeDetailTab() !== 'designer'"
            [label]="t('workflow.detail.tabDesigner')"
            (click)="activeDetailTab.set('designer')">
          </button>
          <button
            type="button"
            pButton
            severity="secondary"
            [outlined]="activeDetailTab() !== 'canonical'"
            [label]="t('workflow.detail.tabCanonicalGraph')"
            (click)="activeDetailTab.set('canonical'); loadCanonicalGraph()">
          </button>
        </div>

        @if (activeDetailTab() === 'designer') {
          <section class="designer-card">
            <h3>{{ t('workflow.processDefinition') }}</h3>
            <div class="meta-grid">
              <input pInputText [(ngModel)]="definitionName" [placeholder]="t('columns.name')" />
              <input pInputText [(ngModel)]="definitionCategory" [placeholder]="t('columns.category')" />
              <select [(ngModel)]="definitionStartType">
                @for (item of definitionStartTypeOptions; track item) {
                  <option [value]="item">{{ item }}</option>
                }
              </select>
              <select [(ngModel)]="definitionResultType">
                @for (item of definitionResultTypeOptions; track item) {
                  <option [value]="item">{{ item }}</option>
                }
              </select>
            </div>
            <textarea pTextarea [(ngModel)]="definitionDescription" rows="2" class="w-full" [placeholder]="t('columns.description')"></textarea>
          </section>

          <section class="designer-card">
            <h3>{{ t('workflow.designer.title') }}</h3>
            <p class="text-muted">{{ t('workflow.designer.description') }}</p>
            <div class="actions">
              <button pButton type="button" severity="secondary" [label]="t('workflow.designer.render')" (click)="renderDiagram()"></button>
              <button pButton type="button" severity="secondary" [label]="t('workflow.designer.syncXml')" (click)="syncXmlFromDiagram()"></button>
              <button pButton type="button" severity="secondary" [label]="t('workflow.designer.validate')" (click)="validateAndNotify()"></button>
            </div>
            @if (diagramMessage()) {
              <p class="diagram-message">{{ diagramMessage() }}</p>
            }

            <div class="designer-workspace">
              <div #bpmnCanvas class="bpmn-canvas"></div>
              <section class="properties-panel">
                <h4>{{ t('workflow.designer.propertiesTitle') }}</h4>
                @if (!selectedElementId()) {
                  <p class="text-muted">{{ t('workflow.designer.selectElementPrompt') }}</p>
                } @else {
                  <div class="properties-grid">
                    <label>{{ t('columns.name') }}</label>
                    <input pInputText [(ngModel)]="selectedElementName" (ngModelChange)="onSelectedPropertiesChanged()" />

                    <label>{{ t('workflow.designer.elementType') }}</label>
                    <input pInputText [ngModel]="selectedElementType" readonly />

                    <label>{{ t('workflow.designer.bpmnElementId') }}</label>
                    <input pInputText [ngModel]="selectedElementId()" readonly />

                    <label>{{ t('columns.description') }}</label>
                    <textarea pTextarea rows="3" [(ngModel)]="selectedElementDescription" (ngModelChange)="onSelectedPropertiesChanged()"></textarea>

                    @if (selectedElementIsTask()) {
                      <label>{{ t('workflow.designer.sourceType') }}</label>
                      <select [(ngModel)]="selectedOperatorType" (ngModelChange)="onSelectedOperatorTypeChanged()">
                        @for (item of operatorTypes; track item) {
                          <option [value]="item">{{ item }}</option>
                        }
                      </select>

                      @if (selectedOperatorType !== 'ModuleSurface') {
                        <label>{{ t('workflow.designer.referenceType') }}</label>
                        <select [(ngModel)]="selectedReferenceType" (ngModelChange)="onSelectedPropertiesChanged()">
                          <option value="none">{{ t('workflow.designer.referenceType.none') }}</option>
                          <option value="form">{{ t('workflow.designer.referenceType.form') }}</option>
                        </select>

                        <label>{{ t('workflow.designer.reference') }}</label>
                        <select [(ngModel)]="selectedReferenceId" [disabled]="selectedReferenceType !== 'form'" (ngModelChange)="onSelectedPropertiesChanged()">
                          <option value="">{{ t('common.select') }}</option>
                          @for (form of formReferences(); track form.id) {
                            <option [value]="form.id">{{ form.name }}</option>
                          }
                        </select>
                      }

                      <label>{{ t('workflow.designer.partyType') }}</label>
                      <p-select
                        class="party-kind-select"
                        [options]="partyKindOptions"
                        optionValue="value"
                        [(ngModel)]="selectedPartyKind"
                        (ngModelChange)="onSelectedPartyKindChanged()"
                        [showClear]="true"
                        appendTo="body"
                        [placeholder]="t('common.select')">
                        <ng-template pTemplate="selectedItem" let-item>
                          {{ item ? t(item.labelKey) : '' }}
                        </ng-template>
                        <ng-template pTemplate="item" let-item>
                          {{ t(item.labelKey) }}
                        </ng-template>
                      </p-select>

                      <label>{{ t('workflow.designer.partySelector') }}</label>
                      @if (selectedPartyKind !== null) {
                        <ain-party-selector
                          class="party-selector"
                          [partyKind]="selectedPartyKind"
                          [(ngModel)]="selectedPartyId"
                          (ngModelChange)="onSelectedPropertiesChanged()" />
                      } @else {
                        <input
                          pInputText
                          class="party-selector-disabled"
                          disabled
                          [placeholder]="t('workflow.designer.partySelectorDisabled')" />
                      }

                      <label>{{ t('workflow.designer.dueDays') }}</label>
                      <input pInputText type="number" [(ngModel)]="selectedDueDays" (ngModelChange)="onSelectedPropertiesChanged()" />

                      <label>{{ t('workflow.designer.dueHours') }}</label>
                      <input pInputText type="number" [(ngModel)]="selectedDueHours" (ngModelChange)="onSelectedPropertiesChanged()" />

                      <label>{{ t('workflow.designer.dueMinutes') }}</label>
                      <input pInputText type="number" [(ngModel)]="selectedDueMinutes" (ngModelChange)="onSelectedPropertiesChanged()" />

                      <label>{{ t('workflow.designer.metadataJson') }}</label>
                      <textarea pTextarea rows="4" [(ngModel)]="selectedMetadataJson" (ngModelChange)="onSelectedPropertiesChanged()"></textarea>

                      @if (isServiceOperatorType(selectedOperatorType)) {
                        <ain-workflow-service-operator-panel
                          [operatorType]="selectedOperatorType === 'SystemTask' ? 'SystemTask' : 'Service'"
                          [configJson]="selectedConfigJson"
                          (configJsonChange)="onOperatorConfigJsonChanged($event)" />
                      } @else if (selectedOperatorType === 'ApiCall') {
                        <ain-workflow-api-call-operator-panel
                          [configJson]="selectedConfigJson"
                          (configJsonChange)="onOperatorConfigJsonChanged($event)" />
                      } @else if (selectedOperatorType === 'AiAgent' || selectedOperatorType === 'AiPrompt') {
                        <ain-workflow-ai-operator-panel
                          [operatorType]="selectedOperatorType"
                          [configJson]="selectedConfigJson"
                          (configJsonChange)="onOperatorConfigJsonChanged($event)" />
                      } @else if (selectedOperatorType === 'ModuleSurface') {
                        <ain-workflow-module-surface-operator-panel
                          [configJson]="selectedConfigJson"
                          (configJsonChange)="onOperatorConfigJsonChanged($event)" />
                      }

                      @if (isInteractiveOperatorType(selectedOperatorType)) {
                        <ain-workflow-action-routing-panel
                          [configJson]="selectedConfigJson"
                          [outgoingFlows]="outgoingFlowOptionsForSelectedOperator()"
                          (configJsonChange)="onOperatorConfigJsonChanged($event)"
                          (flowConditionApply)="applySuggestedFlowCondition($event)" />
                      }

                      <label>
                        <input type="checkbox" [(ngModel)]="showAdvancedConfigJson" />
                        {{ t('workflow.designer.operators.advancedJson') }}
                      </label>
                      @if (showAdvancedConfigJson) {
                        <label>{{ t('workflow.designer.configJson') }}</label>
                        <textarea pTextarea rows="4" [(ngModel)]="selectedConfigJson" (ngModelChange)="onSelectedPropertiesChanged()"></textarea>
                      }
                    } @else if (selectedElementIsStartEvent()) {
                      <label>{{ t('workflow.designer.startType') }}</label>
                      <select [(ngModel)]="selectedStartType" (ngModelChange)="onSelectedPropertiesChanged()">
                        @for (item of definitionStartTypeOptions; track item) {
                          <option [value]="item">{{ item }}</option>
                        }
                      </select>

                      <ain-workflow-trigger-config-panel
                        [triggerType]="selectedTriggerType()"
                        [configJson]="selectedConfigJson"
                        (configJsonChange)="onOperatorConfigJsonChanged($event)" />

                      <label>
                        <input type="checkbox" [(ngModel)]="showAdvancedConfigJson" />
                        {{ t('workflow.designer.operators.advancedJson') }}
                      </label>
                      @if (showAdvancedConfigJson) {
                        <label>{{ t('workflow.designer.configJson') }}</label>
                        <textarea pTextarea rows="4" [(ngModel)]="selectedConfigJson" (ngModelChange)="onSelectedPropertiesChanged()"></textarea>
                      }
                    } @else if (selectedElementIsEndEvent()) {
                      <label>{{ t('workflow.designer.resultType') }}</label>
                      <select [(ngModel)]="selectedEndResultType" (ngModelChange)="onSelectedPropertiesChanged()">
                        @for (item of definitionResultTypeOptions; track item) {
                          <option [value]="item">{{ item }}</option>
                        }
                      </select>

                      <label>{{ t('workflow.designer.configJson') }}</label>
                      <textarea pTextarea rows="4" [(ngModel)]="selectedConfigJson" (ngModelChange)="onSelectedPropertiesChanged()"></textarea>
                    } @else if (selectedElementIsGateway()) {
                      <label>{{ t('workflow.designer.gatewayType') }}</label>
                      <select [(ngModel)]="selectedGatewayType" (ngModelChange)="onSelectedPropertiesChanged()">
                        <option value="ExclusiveGateway">{{ t('workflow.designer.gatewayType.exclusive') }}</option>
                        <option value="ParallelGateway">{{ t('workflow.designer.gatewayType.parallel') }}</option>
                        <option value="InclusiveGateway">{{ t('workflow.designer.gatewayType.inclusive') }}</option>
                        <option value="EventBasedGateway">{{ t('workflow.designer.gatewayType.eventBased') }}</option>
                      </select>

                      <label>{{ t('workflow.designer.defaultFlow') }}</label>
                      <select [(ngModel)]="selectedGatewayDefaultFlowId" (ngModelChange)="onSelectedPropertiesChanged()">
                        <option value="">{{ t('common.select') }}</option>
                        @for (flow of outgoingFlowOptionsForSelectedGateway(); track flow.id) {
                          <option [value]="flow.id">{{ flow.name || flow.id }}</option>
                        }
                      </select>

                      <label>{{ t('workflow.designer.metadataJson') }}</label>
                      <textarea pTextarea rows="4" [(ngModel)]="selectedGatewayMetadataJson" (ngModelChange)="onSelectedPropertiesChanged()"></textarea>

                      <label>{{ t('workflow.designer.configJson') }}</label>
                      <textarea pTextarea rows="4" [(ngModel)]="selectedConfigJson" (ngModelChange)="onSelectedPropertiesChanged()"></textarea>
                    } @else if (selectedElementIsSequenceFlow()) {
                      <label>{{ t('workflow.designer.sourceElement') }}</label>
                      <input pInputText [ngModel]="selectedFlowSourceElementId" readonly />

                      <label>{{ t('workflow.designer.targetElement') }}</label>
                      <input pInputText [ngModel]="selectedFlowTargetElementId" readonly />

                      <label>{{ t('workflow.designer.conditionType') }}</label>
                      <select [(ngModel)]="selectedFlowConditionType" (ngModelChange)="onSelectedPropertiesChanged()">
                        <option value="Expression">{{ t('workflow.designer.conditionType.expression') }}</option>
                        <option value="Script">{{ t('workflow.designer.conditionType.script') }}</option>
                      </select>

                      <label>{{ t('workflow.designer.conditionExpression') }}</label>
                      <textarea pTextarea rows="3" [(ngModel)]="selectedFlowConditionExpression" (ngModelChange)="onSelectedPropertiesChanged()"></textarea>

                      <label>
                        <input type="checkbox" [ngModel]="selectedFlowIsDefault" (ngModelChange)="selectedFlowIsDefault = !!$event; onSelectedPropertiesChanged()" />
                        {{ t('workflow.designer.isDefaultFlow') }}
                      </label>

                      <label>{{ t('workflow.designer.configJson') }}</label>
                      <textarea pTextarea rows="4" [(ngModel)]="selectedConfigJson" (ngModelChange)="onSelectedPropertiesChanged()"></textarea>
                    } @else {
                      <label>{{ t('workflow.designer.configJson') }}</label>
                      <textarea pTextarea rows="4" [(ngModel)]="selectedConfigJson" (ngModelChange)="onSelectedPropertiesChanged()"></textarea>
                    }
                  </div>

                  <button pButton type="button" [label]="t('workflow.designer.applyProperties')" (click)="applySelectedProperties()"></button>
                }
              </section>
            </div>
          </section>

          <p><strong>{{ t('columns.status') }}:</strong> {{ def.status }} | {{ t('columns.version') }}: {{ def.currentVersion }}</p>
          <label>{{ t('workflow.bpmnXml') }}</label>
          <textarea pTextarea [(ngModel)]="bpmnXml" (ngModelChange)="onBpmnXmlEdited()" rows="8" class="w-full"></textarea>
          <label>{{ t('workflow.versionNotes') }}</label>
          <textarea pTextarea [(ngModel)]="versionNotes" rows="3" class="w-full"></textarea>
          <button pButton type="button" class="mt" [label]="t('workflow.addVersion')" (click)="addVersion()"></button>
          <h3>{{ t('workflow.versions') }}</h3>
          <ul>
            @for (v of def.versions; track v.id) {
              <li class="version-item">
                <span>v{{ v.version }} — {{ v.status }} @if (v.isDefault) { (default) }</span>
                <button pButton type="button" size="small" severity="secondary" [label]="t('workflow.versions.load')" (click)="loadVersionIntoDesigner(v.id)"></button>
              </li>
            }
          </ul>
        } @else {
          <section class="designer-card">
            <h3>{{ t('workflow.canonicalGraph.title') }}</h3>
            <p class="text-muted">{{ t('workflow.canonicalGraph.description') }}</p>
            <div class="canonical-graph-toolbar">
              <label>{{ t('workflow.canonicalGraph.version') }}</label>
              <select [(ngModel)]="selectedCanonicalGraphVersionId" (ngModelChange)="loadCanonicalGraph()">
                @for (v of def.versions; track v.id) {
                  <option [value]="v.id">v{{ v.version }} — {{ v.status }} @if (v.isDefault) { (default) }</option>
                }
              </select>
              <button pButton type="button" severity="secondary" [label]="t('workflow.canonicalGraph.refresh')" (click)="loadCanonicalGraph()"></button>
            </div>

            @if (canonicalGraphLoading()) {
              <ain-state type="loading" [title]="t('common.loading')" />
            } @else if (canonicalGraphError()) {
              <ain-state type="error" [title]="canonicalGraphError() ?? ''" />
            } @else if (canonicalGraph(); as graph) {
              <div class="canonical-graph-counts">
                <span>{{ t('workflow.canonicalGraph.elements') }}: {{ graph.elements.length }}</span>
                <span>{{ t('workflow.canonicalGraph.flows') }}: {{ graph.flows.length }}</span>
                <span>{{ t('workflow.canonicalGraph.variableDefinitions') }}: {{ graph.variableDefinitions.length }}</span>
                <span>{{ t('workflow.canonicalGraph.variableMappings') }}: {{ graph.variableMappings.length }}</span>
                <span>{{ t('workflow.canonicalGraph.resourceBindings') }}: {{ graph.resourceBindings.length }}</span>
              </div>
              <div class="canonical-graph-sections">
                <article>
                  <h4>{{ t('workflow.canonicalGraph.elements') }}</h4>
                  <pre>{{ formatCanonicalGraphJson(graph.elements) }}</pre>
                </article>
                <article>
                  <h4>{{ t('workflow.canonicalGraph.flows') }}</h4>
                  <pre>{{ formatCanonicalGraphJson(graph.flows) }}</pre>
                </article>
                <article>
                  <h4>{{ t('workflow.canonicalGraph.variableDefinitions') }}</h4>
                  <pre>{{ formatCanonicalGraphJson(graph.variableDefinitions) }}</pre>
                </article>
                <article>
                  <h4>{{ t('workflow.canonicalGraph.variableMappings') }}</h4>
                  <pre>{{ formatCanonicalGraphJson(graph.variableMappings) }}</pre>
                </article>
                <article>
                  <h4>{{ t('workflow.canonicalGraph.resourceBindings') }}</h4>
                  <pre>{{ formatCanonicalGraphJson(graph.resourceBindings) }}</pre>
                </article>
              </div>
            } @else {
              <ain-state type="empty" [title]="t('workflow.canonicalGraph.empty')" />
            }
          </section>
        }
      }

      <p-dialog
        [header]="t('workflow.versions.load')"
        [visible]="versionLoadDialogOpen()"
        [modal]="true"
        [style]="{ width: '36rem', maxWidth: '95vw' }"
        (visibleChange)="onVersionLoadDialogVisibleChange($event)">
        <p class="version-load-warning">{{ versionLoadWarningText() }}</p>
        <div class="dialog-footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="closeVersionLoadDialog()"></button>
          <button pButton type="button" [label]="t('workflow.versions.load')" (click)="confirmVersionLoad()"></button>
        </div>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .actions { display: flex; gap: 12px; margin: 16px 0; }
    .detail-tabs { display: flex; gap: 8px; margin-bottom: 12px; }
    .designer-card { border: 1px solid #e5e7eb; border-radius: 10px; padding: 16px; margin-bottom: 16px; }
    .designer-row { display: grid; grid-template-columns: 1fr 180px 1.5fr auto; gap: 8px; margin-bottom: 8px; }
    .operator-row { grid-template-columns: 1fr 170px 180px 1.2fr auto; }
    .meta-grid { display: grid; grid-template-columns: 1fr 1fr 180px 180px; gap: 8px; margin-bottom: 8px; }
    .designer-workspace { display: flex; flex-direction: column; gap: 16px; width: 100%; }
    .w-full { width: 100%; }
    .mt { margin-top: 12px; }
    .text-muted { color: #6b7280; }
    .diagram-message { color: #b91c1c; margin-bottom: 8px; }
    .bpmn-canvas { width: 100%; height: 560px; min-height: 560px; border: 1px solid #e5e7eb; border-radius: 8px; background: #fff; overflow: hidden; }
    .bpmn-canvas .djs-container,
    .bpmn-canvas .djs-parent,
    .bpmn-canvas .djs-palette { z-index: 1; }
    .properties-panel { border: 1px solid #e5e7eb; border-radius: 8px; padding: 16px; background: #f8fafc; width: 100%; }
    .properties-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px 16px; margin-bottom: 10px; align-items: start; }
    .properties-grid label { font-size: 12px; color: #475569; display: block; margin-bottom: 4px; }
    .properties-grid textarea,
    .properties-grid select,
    .properties-grid input:not([type='checkbox']) { width: 100%; min-height: 40px; border: 1px solid #cbd5e1; border-radius: 8px; padding: 8px; box-sizing: border-box; }
    .properties-grid .party-kind-select,
    .properties-grid .party-selector,
    .properties-grid .party-selector-disabled { grid-column: span 1; }
    .properties-grid ain-workflow-service-operator-panel,
    .properties-grid ain-workflow-api-call-operator-panel,
    .properties-grid ain-workflow-ai-operator-panel,
    .properties-grid ain-workflow-trigger-config-panel,
    .properties-grid ain-workflow-action-routing-panel,
    .properties-grid ain-workflow-module-surface-operator-panel,
    .properties-grid textarea[rows],
    .properties-grid label:has(input[type='checkbox']) { grid-column: 1 / -1; }
    .version-item { display: flex; gap: 8px; align-items: center; justify-content: space-between; margin: 6px 0; }
    .canonical-graph-toolbar { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; margin-bottom: 12px; }
    .canonical-graph-toolbar label { font-size: 12px; color: #475569; }
    .canonical-graph-toolbar select { min-width: 260px; min-height: 40px; border: 1px solid #cbd5e1; border-radius: 8px; padding: 8px; }
    .canonical-graph-counts { display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 12px; color: #334155; font-size: 13px; }
    .canonical-graph-sections { display: grid; gap: 12px; }
    .canonical-graph-sections article { border: 1px solid #e2e8f0; border-radius: 8px; padding: 12px; background: #f8fafc; }
    .canonical-graph-sections h4 { margin: 0 0 8px 0; font-size: 14px; }
    .canonical-graph-sections pre { margin: 0; max-height: 260px; overflow: auto; background: #0f172a; color: #e2e8f0; border-radius: 8px; padding: 10px; font-size: 11px; line-height: 1.45; }
    .version-load-warning { white-space: pre-line; margin: 0; font-size: 13px; line-height: 1.5; color: #334155; }
    .dialog-footer { display: flex; justify-content: flex-end; gap: 8px; margin-top: 12px; }
    a { display: inline-block; margin-bottom: 12px; }
    @media (max-width: 900px) {
      .properties-grid { grid-template-columns: 1fr; }
    }
  `]
})
export class WorkflowDefinitionDetailComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('bpmnCanvas') bpmnCanvas?: ElementRef<HTMLDivElement>;

  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly api = inject(WorkflowApiService);
  private readonly dynamicApi = inject(DynamicApiService);
  private readonly localization = inject(LocalizationService);
  private readonly messages = inject(MessageService);
  private readonly ngZone = inject(NgZone);
  private readonly surfaceRegistry = inject(WorkflowModuleSurfaceRegistry);
  private bpmnModeler: {
    importXML: (xml: string) => Promise<unknown>;
    saveXML: (options?: Record<string, unknown>) => Promise<{ xml: string }>;
    get: (service: string) => any;
    on: (event: string, callback: (event: any) => void) => void;
    destroy: () => void;
  } | null = null;
  private modelerEventsBound = false;
  private isApplyingProperties = false;
  private readonly debugEvents = true;

  readonly definition = signal<ProcessDefinitionDetail | null>(null);
  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly activeDetailTab = signal<'designer' | 'canonical'>('designer');
  readonly canonicalGraph = signal<ProcessVersionCanonicalGraph | null>(null);
  readonly canonicalGraphLoading = signal(false);
  readonly canonicalGraphError = signal<string | null>(null);
  readonly diagramMessage = signal<string | null>(null);
  readonly versionLoadDialogOpen = signal(false);
  readonly versionLoadWarningText = signal('');
  readonly startTriggers = signal<OrchestratorStartTrigger[]>([]);
  readonly operators = signal<OrchestratorOperator[]>([]);
  readonly gateways = signal<OrchestratorGateway[]>([]);
  readonly flows = signal<OrchestratorFlow[]>([]);
  readonly formReferences = signal<DynamicForm[]>([]);

  readonly triggerTypes = ['Manual', 'Event', 'System', 'Api', 'Schedule', 'Message', 'AiSignal'];
  readonly operatorTypes = ['Form', 'UserTask', 'ModuleSurface', 'Scheduler', 'SystemTask', 'Service', 'ApiCall', 'AiAgent', 'AiPrompt', 'Decision'];
  readonly definitionStartTypeOptions = ['manual', 'event', 'system', 'api', 'schedule', 'message', 'aiSignal'];
  readonly definitionResultTypeOptions = ['approval', 'completed', 'error', 'terminate', 'message', 'signal'];

  bpmnXml = DEFAULT_BPMN_XML;
  versionNotes = '';
  definitionName = '';
  definitionDescription = '';
  definitionCategory = '';
  definitionStartType = 'manual';
  definitionResultType = 'approval';
  readonly selectedElementId = signal('');
  selectedElementType = '';
  selectedElementName = '';
  selectedElementDescription = '';
  selectedOperatorType = 'UserTask';
  selectedReferenceType = 'none';
  selectedReferenceId = '';
  readonly partyKindOptions = PLATFORM_PARTY_KIND_OPTIONS;

  selectedPartyKind: number | null = null;
  selectedPartyId = '';
  selectedDueDays = 0;
  selectedDueHours = 0;
  selectedDueMinutes = 0;
  selectedMetadataJson = '{}';
  selectedConfigJson = '{}';
  selectedStartType = 'manual';
  private pendingVersionLoad: ProcessVersionDetail | null = null;
  selectedEndResultType = 'approval';
  selectedGatewayType = 'ExclusiveGateway';
  selectedGatewayDefaultFlowId = '';
  selectedGatewayMetadataJson = '{}';
  selectedFlowConditionType = 'Expression';
  selectedFlowConditionExpression = '';
  selectedFlowIsDefault = false;
  selectedFlowSourceElementId = '';
  selectedFlowTargetElementId = '';
  selectedCanonicalGraphVersionId = '';
  showAdvancedConfigJson = false;
  private baselineBpmnXml = '';
  private baselineDesignSnapshot = '';
  private bpmnTextareaDirty = false;

  readonly isServiceOperatorType = isServiceOperatorType;
  readonly isInteractiveOperatorType = isInteractiveOperatorType;

  ngOnInit(): void {
    this.loadFormReferences();
    this.reload();
  }

  async ngAfterViewInit(): Promise<void> {
    await this.initModeler();
  }

  ngOnDestroy(): void {
    this.bpmnModeler?.destroy();
    this.bpmnModeler = null;
    this.modelerEventsBound = false;
  }

  reload(): void {
    const id = this.route.snapshot.paramMap.get('id')!;
    this.loading.set(true);
    this.error.set(null);
    this.api.getDefinition(id).subscribe({
      next: def => {
        this.definition.set(def);
        this.bpmnXml = def.latestBpmnXml || this.bpmnXml;
        this.versionNotes = def.latestNotes || '';
        this.definitionName = def.name;
        this.definitionDescription = def.description ?? '';
        this.definitionCategory = def.category ?? '';
        this.definitionStartType = def.startType || 'manual';
        this.definitionResultType = def.resultType || 'approval';
        this.loadOrchestratorSnapshot(def.latestParsedGraphSnapshot);
        this.selectedCanonicalGraphVersionId = this.resolveInitialCanonicalGraphVersionId(def);
        this.canonicalGraph.set(null);
        this.canonicalGraphError.set(null);
        if (this.selectedCanonicalGraphVersionId) {
          this.loadCanonicalGraph();
        }
        this.storeDesignBaseline();
        if (this.bpmnModeler) {
          void this.renderDiagram();
        }
        this.loading.set(false);
      },
      error: () => {
        this.error.set(this.t('common.loadFailed'));
        this.loading.set(false);
      }
    });
  }

  loadCanonicalGraph(): void {
    const definition = this.definition();
    if (!definition) return;

    const selectedVersionId = this.selectedCanonicalGraphVersionId || this.resolveInitialCanonicalGraphVersionId(definition);
    this.selectedCanonicalGraphVersionId = selectedVersionId;
    if (!selectedVersionId) {
      this.canonicalGraph.set(null);
      this.canonicalGraphError.set(this.t('workflow.canonicalGraph.noVersion'));
      return;
    }

    this.canonicalGraphLoading.set(true);
    this.canonicalGraphError.set(null);
    this.api.getVersionCanonicalGraph(definition.id, selectedVersionId).subscribe({
      next: graph => {
        this.canonicalGraph.set(graph);
        this.canonicalGraphLoading.set(false);
      },
      error: () => {
        this.canonicalGraph.set(null);
        this.canonicalGraphError.set(this.t('workflow.canonicalGraph.loadFailed'));
        this.canonicalGraphLoading.set(false);
      }
    });
  }

  formatCanonicalGraphJson(value: unknown): string {
    try {
      const pretty = JSON.stringify(value, null, 2);
      return pretty.length > 12000 ? `${pretty.slice(0, 12000)}\n...` : pretty;
    } catch {
      return '{}';
    }
  }

  publish(): void {
    const id = this.definition()?.id;
    if (!id) return;
    const validationErrors = this.validateCurrentDefinition();
    if (validationErrors.length > 0) {
      this.messages.add({ severity: 'error', summary: validationErrors[0] });
      return;
    }
    this.api.publishDefinition(id).subscribe({
      next: () => {
        this.messages.add({ severity: 'success', summary: this.t('workflow.published') });
        this.reload();
      },
      error: err => this.messages.add({ severity: 'error', summary: this.resolveWorkflowApiError(err) })
    });
  }

  async saveDefinition(): Promise<void> {
    const id = this.definition()?.id;
    if (!id || !this.definitionName.trim()) return;

    await this.prepareDesignForSave();
    const validationErrors = this.validateCurrentDefinition();
    if (validationErrors.length > 0) {
      this.messages.add({ severity: 'error', summary: validationErrors[0] });
      return;
    }

    const designChanged = this.hasDesignChanged();
    this.api.updateDefinition(id, {
      name: this.definitionName.trim(),
      description: this.definitionDescription.trim(),
      category: this.definitionCategory.trim(),
      startType: this.definitionStartType,
      resultType: this.definitionResultType
    }).subscribe({
      next: () => {
        if (!designChanged) {
          this.messages.add({ severity: 'success', summary: this.t('common.saved') });
          this.reload();
          return;
        }

        const parsedGraphSnapshot = this.buildParsedGraphSnapshot();
        this.api.addVersionWithSnapshot(id, this.bpmnXml, parsedGraphSnapshot, this.versionNotes).subscribe({
          next: () => {
            this.storeDesignBaseline();
            this.messages.add({ severity: 'success', summary: this.t('workflow.versionAutoCreated') });
            this.reload();
          },
          error: err => this.messages.add({ severity: 'error', summary: this.resolveWorkflowApiError(err) })
        });
      },
      error: err => this.messages.add({ severity: 'error', summary: this.resolveWorkflowApiError(err) })
    });
  }

  archive(): void {
    const id = this.definition()?.id;
    if (!id) return;

    this.api.archiveDefinition(id).subscribe({
      next: () => {
        this.messages.add({ severity: 'success', summary: this.t('workflow.archived') });
        this.reload();
      },
      error: () => this.messages.add({ severity: 'error', summary: this.t('common.saveFailed') })
    });
  }

  async addVersion(): Promise<void> {
    const id = this.definition()?.id;
    if (!id) return;

    await this.prepareDesignForSave();
    const validationErrors = this.validateCurrentDefinition();
    if (validationErrors.length > 0) {
      this.messages.add({ severity: 'error', summary: validationErrors[0] });
      return;
    }
    if (!this.hasDesignChanged()) {
      this.messages.add({ severity: 'warn', summary: this.t('workflow.versionNoChanges') });
      return;
    }
    const parsedGraphSnapshot = this.buildParsedGraphSnapshot();

    this.api.addVersionWithSnapshot(id, this.bpmnXml, parsedGraphSnapshot, this.versionNotes).subscribe({
      next: () => {
        this.storeDesignBaseline();
        this.messages.add({ severity: 'success', summary: this.t('workflow.versionCreated') });
        this.reload();
      },
      error: err => this.messages.add({ severity: 'error', summary: this.resolveWorkflowApiError(err) })
    });
  }

  onBpmnXmlEdited(): void {
    this.bpmnTextareaDirty = true;
  }

  validateAndNotify(): void {
    const errors = this.validateCurrentDefinition();
    const warnings = this.collectDefinitionWarnings();
    if (errors.length === 0 && warnings.length === 0) {
      this.messages.add({ severity: 'success', summary: this.t('workflow.designer.validationPassed') });
      return;
    }

    for (const error of errors.slice(0, 3)) {
      this.messages.add({ severity: 'error', summary: error });
    }
    for (const warning of warnings.slice(0, 3)) {
      this.messages.add({ severity: 'warn', summary: warning });
    }
  }

  loadVersionIntoDesigner(versionId: string): void {
    const definitionId = this.definition()?.id;
    if (!definitionId || !versionId) return;

    this.api.getVersion(definitionId, versionId).subscribe({
      next: version => {
        const hasUnsavedChanges = this.hasDesignChanged();
        if (hasUnsavedChanges) {
          this.pendingVersionLoad = version;
          this.versionLoadWarningText.set(this.buildVersionLoadWarningMessage(version));
          this.versionLoadDialogOpen.set(true);
          return;
        }
        this.applyVersionToDesigner(version);
      },
      error: err => this.messages.add({ severity: 'error', summary: this.resolveWorkflowApiError(err) })
    });
  }

  onVersionLoadDialogVisibleChange(visible: boolean): void {
    if (!visible) this.closeVersionLoadDialog();
  }

  closeVersionLoadDialog(): void {
    this.versionLoadDialogOpen.set(false);
    this.pendingVersionLoad = null;
    this.versionLoadWarningText.set('');
  }

  confirmVersionLoad(): void {
    const version = this.pendingVersionLoad;
    if (!version) return;
    this.applyVersionToDesigner(version);
    this.closeVersionLoadDialog();
  }

  startInstance(): void {
    const def = this.definition();
    if (!def) return;
    this.api.startInstance(def.id, `manual-${Date.now()}`).subscribe({
      next: inst => {
        this.messages.add({ severity: 'success', summary: this.t('workflow.instanceStarted') });
        void this.router.navigate(['/workflow/instances', inst.id]);
      },
      error: () => this.messages.add({ severity: 'error', summary: this.t('common.saveFailed') })
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  addTrigger(): void {
    this.startTriggers.update(items => [
      ...items,
      {
        id: crypto.randomUUID(),
        name: '',
        type: 'Manual',
        configurationJson: '{}'
      }
    ]);
  }

  removeTrigger(id: string): void {
    this.startTriggers.update(items => items.filter(item => item.id !== id));
  }

  addOperator(): void {
    this.operators.update(items => [
      ...items,
      {
        id: crypto.randomUUID(),
        name: '',
        type: 'UserTask',
        bpmnElementId: '',
        description: '',
        referenceType: 'none',
        referenceId: null,
        assigneeType: null,
        assigneeId: null,
        dueDays: 0,
        dueHours: 0,
        dueMinutes: 0,
        metadataJson: '{}',
        configurationJson: '{}'
      }
    ]);
  }

  removeOperator(id: string): void {
    this.operators.update(items => items.filter(item => item.id !== id));
  }

  selectedElementIsTask(): boolean {
    return this.isTaskElementType(this.selectedElementType);
  }

  selectedElementIsStartEvent(): boolean {
    return this.isStartEventType(this.selectedElementType);
  }

  selectedElementIsEndEvent(): boolean {
    return this.isEndEventType(this.selectedElementType);
  }

  selectedElementIsGateway(): boolean {
    return this.isGatewayElementType(this.selectedElementType);
  }

  selectedElementIsSequenceFlow(): boolean {
    return this.isSequenceFlowElementType(this.selectedElementType);
  }

  outgoingFlowOptionsForSelectedGateway(): OrchestratorFlow[] {
    const gatewayId = this.selectedElementId().trim();
    if (!gatewayId) return [];
    return this.flows()
      .filter(flow => flow.sourceElementId === gatewayId)
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  outgoingFlowOptionsForSelectedOperator(): WorkflowOutgoingFlowOption[] {
    const operatorElementId = this.selectedElementId().trim();
    if (!operatorElementId) return [];
    return this.flows()
      .filter(flow => flow.sourceElementId === operatorElementId)
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(flow => ({
        id: flow.bpmnElementId,
        name: flow.name || flow.bpmnElementId,
        bpmnElementId: flow.bpmnElementId
      }));
  }

  selectedTriggerType(): string {
    return this.toPascalCase(this.selectedStartType);
  }

  onOperatorConfigJsonChanged(configJson: string): void {
    this.selectedConfigJson = configJson;
    this.onSelectedPropertiesChanged();
  }

  applySuggestedFlowCondition(event: { flowId: string; conditionExpression: string }): void {
    const flow = this.flows().find(item => item.bpmnElementId === event.flowId);
    if (!flow) return;

    const updatedFlow: OrchestratorFlow = {
      ...flow,
      conditionType: 'Expression',
      conditionExpression: event.conditionExpression
    };
    this.flows.update(items => items.map(item => item.id === flow.id ? updatedFlow : item));

    if (this.selectedElementIsSequenceFlow() && this.selectedElementId() === flow.bpmnElementId) {
      this.selectedFlowConditionType = 'Expression';
      this.selectedFlowConditionExpression = event.conditionExpression;
    }

    this.messages.add({
      severity: 'info',
      summary: this.t('workflow.designer.operators.actions.conditionApplied')
    });
  }

  private async initModeler(): Promise<void> {
    if (!this.bpmnCanvas) return;
    if (this.bpmnModeler) return;

    try {
      await loadBpmnJs();
      await loadBpmnStyles();
      if (!window.BpmnJS) throw new Error('bpmn_js_not_loaded');
      const CustomPaletteProvider = function(
        this: any,
        palette: any,
        create: any,
        elementFactory: any,
        spaceTool: any,
        lassoTool: any,
        handTool: any,
        globalConnect: any,
        translate: any
      ) {
        this._create = create;
        this._elementFactory = elementFactory;
        this._spaceTool = spaceTool;
        this._lassoTool = lassoTool;
        this._handTool = handTool;
        this._globalConnect = globalConnect;
        this._translate = translate;
        palette.registerProvider(this);
      };

      (CustomPaletteProvider as any).$inject = [
        'palette',
        'create',
        'elementFactory',
        'spaceTool',
        'lassoTool',
        'handTool',
        'globalConnect',
        'translate'
      ];

      (CustomPaletteProvider as any).prototype.getPaletteEntries = function() {
        const actions: Record<string, unknown> = {};
        const create = this._create;
        const elementFactory = this._elementFactory;
        const spaceTool = this._spaceTool;
        const lassoTool = this._lassoTool;
        const handTool = this._handTool;
        const globalConnect = this._globalConnect;
        const translate = this._translate;

        const createAction = (type: string, group: string, className: string, title: string) => {
          const createListener = (event: Event) => {
            const shape = elementFactory.createShape({ type });
            create.start(event, shape);
          };
          return {
            group,
            className,
            title,
            action: { dragstart: createListener, click: createListener }
          };
        };

        Object.assign(actions, {
          'hand-tool': {
            group: 'tools',
            className: 'bpmn-icon-hand-tool',
            title: translate('Activate hand tool'),
            action: { click: (event: Event) => handTool.activateHand(event) }
          },
          'lasso-tool': {
            group: 'tools',
            className: 'bpmn-icon-lasso-tool',
            title: translate('Activate lasso tool'),
            action: { click: (event: Event) => lassoTool.activateSelection(event) }
          },
          'space-tool': {
            group: 'tools',
            className: 'bpmn-icon-space-tool',
            title: translate('Activate space tool'),
            action: { click: (event: Event) => spaceTool.activateSelection(event) }
          },
          'global-connect-tool': {
            group: 'tools',
            className: 'bpmn-icon-connection-multi',
            title: translate('Activate global connect tool'),
            action: { click: (event: Event) => globalConnect.toggle(event) }
          },
          'tool-separator': { group: 'tools', separator: true },
          'create.start-event': createAction('bpmn:StartEvent', 'event', 'bpmn-icon-start-event-none', translate('Create Start Event')),
          'create.end-event': createAction('bpmn:EndEvent', 'event', 'bpmn-icon-end-event-none', translate('Create End Event')),
          'create.task': createAction('bpmn:Task', 'activity', 'bpmn-icon-task', translate('Create Task')),
          'create.user-task': createAction('bpmn:UserTask', 'activity', 'bpmn-icon-user-task', translate('Create User Task')),
          'create.service-task': createAction('bpmn:ServiceTask', 'activity', 'bpmn-icon-service-task', translate('Create Service Task')),
          'create.exclusive-gateway': createAction('bpmn:ExclusiveGateway', 'gateway', 'bpmn-icon-gateway-xor', translate('Create Exclusive Gateway')),
          'create.parallel-gateway': createAction('bpmn:ParallelGateway', 'gateway', 'bpmn-icon-gateway-parallel', translate('Create Parallel Gateway')),
          'create.inclusive-gateway': createAction('bpmn:InclusiveGateway', 'gateway', 'bpmn-icon-gateway-or', translate('Create Inclusive Gateway')),
          'create.event-based-gateway': createAction('bpmn:EventBasedGateway', 'gateway', 'bpmn-icon-gateway-eventbased', translate('Create Event-Based Gateway'))
        });

        return actions;
      };

      this.bpmnModeler = new window.BpmnJS({
        container: this.bpmnCanvas.nativeElement,
        additionalModules: [
          {
            __init__: ['paletteProvider'],
            paletteProvider: ['type', CustomPaletteProvider]
          }
        ],
        moddleExtensions: {}
      });

      await this.renderDiagram();
    } catch {
      this.diagramMessage.set(this.t('workflow.designer.loadError'));
    }
  }

  async renderDiagram(): Promise<void> {
    if (!this.bpmnModeler) {
      await this.initModeler();
      return;
    }

    try {
      await this.bpmnModeler.importXML(this.bpmnXml);
      this.fitDiagramToViewport();
      this.bindModelerEvents();
      this.syncArtifactsFromDiagram();
      this.diagramMessage.set(null);
    } catch {
      this.diagramMessage.set(this.t('workflow.designer.renderError'));
    }
  }

  async syncXmlFromDiagram(showToast = true): Promise<void> {
    if (!this.bpmnModeler) return;

    try {
      const result = await this.bpmnModeler.saveXML({ format: true });
      this.bpmnXml = result.xml;
      this.bpmnTextareaDirty = false;
      this.diagramMessage.set(null);
      if (showToast) {
        this.messages.add({ severity: 'success', summary: this.t('workflow.designer.synced') });
      }
    } catch {
      this.diagramMessage.set(this.t('workflow.designer.syncError'));
    }
  }

  applySelectedProperties(): void {
    const elementId = this.selectedElementId().trim();
    if (!elementId || !this.bpmnModeler) return;

    const elementRegistry = this.bpmnModeler.get('elementRegistry');
    const modeling = this.bpmnModeler.get('modeling');
    const element = elementRegistry?.get?.(elementId);
    if (!element) return;

    this.isApplyingProperties = true;
    modeling?.updateProperties?.(element, { name: this.selectedElementName.trim() });

    if (this.isStartEventType(this.selectedElementType)) {
      try {
        if (this.selectedConfigJson.trim()) {
          JSON.parse(this.selectedConfigJson);
        }
      } catch {
        this.messages.add({ severity: 'error', summary: this.t('workflow.designer.invalidConfigJson') });
        this.isApplyingProperties = false;
        return;
      }

      this.definitionStartType = this.selectedStartType;
      this.startTriggers.update(items => {
        const first = items[0];
        const mappedType = this.toPascalCase(this.selectedStartType);
        if (!first) {
          return [{
            id: crypto.randomUUID(),
            name: this.selectedElementName.trim() || 'Start',
            type: mappedType,
            configurationJson: this.selectedConfigJson.trim() || '{}'
          }];
        }
        return [{
          ...first,
          name: this.selectedElementName.trim() || first.name,
          type: mappedType,
          configurationJson: this.selectedConfigJson.trim() || '{}'
        }, ...items.slice(1)];
      });
    } else if (this.isEndEventType(this.selectedElementType)) {
      try {
        if (this.selectedConfigJson.trim()) {
          JSON.parse(this.selectedConfigJson);
        }
      } catch {
        this.messages.add({ severity: 'error', summary: this.t('workflow.designer.invalidConfigJson') });
        this.isApplyingProperties = false;
        return;
      }

      this.definitionResultType = this.selectedEndResultType;
    } else if (this.isTaskElementType(this.selectedElementType)) {
      const operator = this.ensureOperatorForElement(elementId, this.selectedElementName);
      if (!operator) {
        this.isApplyingProperties = false;
        return;
      }

      try {
        if (this.selectedConfigJson.trim()) {
          JSON.parse(this.selectedConfigJson);
        }
      } catch {
        this.messages.add({ severity: 'error', summary: this.t('workflow.designer.invalidConfigJson') });
        this.isApplyingProperties = false;
        return;
      }
      try {
        if (this.selectedMetadataJson.trim()) {
          JSON.parse(this.selectedMetadataJson);
        }
      } catch {
        this.messages.add({ severity: 'error', summary: this.t('workflow.designer.invalidMetadataJson') });
        this.isApplyingProperties = false;
        return;
      }

      const effectiveReferenceType = this.selectedOperatorType === 'Form'
        ? 'form'
        : this.selectedReferenceType;

      const updatedOperator: OrchestratorOperator = this.finalizeOperatorSnapshot({
        ...operator,
        name: this.selectedElementName.trim() || operator.name,
        type: this.selectedOperatorType,
        description: this.selectedElementDescription.trim(),
        referenceType: effectiveReferenceType,
        referenceId: this.selectedReferenceId || null,
        assigneeType: this.selectedPartyKind !== null ? partyKindToApiValue(this.selectedPartyKind) : null,
        assigneeId: this.selectedPartyId.trim() || null,
        dueDays: Math.max(0, this.selectedDueDays || 0),
        dueHours: Math.max(0, this.selectedDueHours || 0),
        dueMinutes: Math.max(0, this.selectedDueMinutes || 0),
        metadataJson: this.selectedMetadataJson.trim() || '{}',
        configurationJson: this.selectedConfigJson.trim() || '{}'
      });

      this.operators.update(items => items.map(item => item.id === operator.id ? updatedOperator : item));
      this.selectedConfigJson = updatedOperator.configurationJson;
      this.selectedMetadataJson = updatedOperator.metadataJson;
    } else if (this.isGatewayElementType(this.selectedElementType)) {
      const gateway = this.ensureGatewayForElement(elementId, this.selectedElementName);
      if (!gateway) {
        this.isApplyingProperties = false;
        return;
      }

      try {
        if (this.selectedConfigJson.trim()) {
          JSON.parse(this.selectedConfigJson);
        }
      } catch {
        this.messages.add({ severity: 'error', summary: this.t('workflow.designer.invalidConfigJson') });
        this.isApplyingProperties = false;
        return;
      }
      try {
        if (this.selectedGatewayMetadataJson.trim()) {
          JSON.parse(this.selectedGatewayMetadataJson);
        }
      } catch {
        this.messages.add({ severity: 'error', summary: this.t('workflow.designer.invalidMetadataJson') });
        this.isApplyingProperties = false;
        return;
      }

      const updatedGateway: OrchestratorGateway = {
        ...gateway,
        name: this.selectedElementName.trim() || gateway.name,
        description: this.selectedElementDescription.trim(),
        type: this.selectedGatewayType,
        defaultFlowId: this.selectedGatewayDefaultFlowId || null,
        metadataJson: this.selectedGatewayMetadataJson.trim() || '{}',
        configurationJson: this.selectedConfigJson.trim() || '{}'
      };

      this.gateways.update(items => items.map(item => item.id === gateway.id ? updatedGateway : item));
      this.selectedConfigJson = updatedGateway.configurationJson;
      this.selectedGatewayMetadataJson = updatedGateway.metadataJson;
    } else if (this.isSequenceFlowElementType(this.selectedElementType)) {
      const flow = this.ensureFlowForElement(element);
      if (!flow) {
        this.isApplyingProperties = false;
        return;
      }

      try {
        if (this.selectedConfigJson.trim()) {
          JSON.parse(this.selectedConfigJson);
        }
      } catch {
        this.messages.add({ severity: 'error', summary: this.t('workflow.designer.invalidConfigJson') });
        this.isApplyingProperties = false;
        return;
      }

      const updatedFlow: OrchestratorFlow = {
        ...flow,
        name: this.selectedElementName.trim() || flow.name,
        description: this.selectedElementDescription.trim(),
        conditionType: this.selectedFlowConditionType,
        conditionExpression: this.selectedFlowConditionExpression,
        isDefault: this.selectedFlowIsDefault,
        configurationJson: this.selectedConfigJson.trim() || '{}'
      };

      this.flows.update(items => items.map(item => item.id === flow.id ? updatedFlow : item));
      this.selectedConfigJson = updatedFlow.configurationJson;
    }

    this.isApplyingProperties = false;
    this.messages.add({ severity: 'success', summary: this.t('common.saved') });
  }

  onSelectedPropertiesChanged(): void {
    const elementId = this.selectedElementId().trim();
    if (!elementId) return;

    if (this.isStartEventType(this.selectedElementType)) {
      this.definitionStartType = this.selectedStartType;
      this.startTriggers.update(items => {
        const first = items[0];
        const mappedType = this.toPascalCase(this.selectedStartType);
        if (!first) {
          return [{
            id: crypto.randomUUID(),
            name: this.selectedElementName.trim() || 'Start',
            type: mappedType,
            configurationJson: this.selectedConfigJson.trim() || '{}'
          }];
        }
        return [{
          ...first,
          name: this.selectedElementName.trim() || first.name,
          type: mappedType,
          configurationJson: this.selectedConfigJson.trim() || '{}'
        }, ...items.slice(1)];
      });
      return;
    }

    if (this.isEndEventType(this.selectedElementType)) {
      this.definitionResultType = this.selectedEndResultType;
      return;
    }

    if (this.isTaskElementType(this.selectedElementType)) {
      const operator = this.ensureOperatorForElement(elementId, this.selectedElementName);
      if (!operator) return;

      const effectiveReferenceType = this.selectedOperatorType === 'Form'
        ? 'form'
        : this.selectedReferenceType;
      const updatedOperator: OrchestratorOperator = this.finalizeOperatorSnapshot({
        ...operator,
        name: this.selectedElementName.trim() || operator.name,
        type: this.selectedOperatorType,
        description: this.selectedElementDescription.trim(),
        referenceType: effectiveReferenceType,
        referenceId: this.selectedReferenceId || null,
        assigneeType: this.selectedPartyKind !== null ? partyKindToApiValue(this.selectedPartyKind) : null,
        assigneeId: this.selectedPartyId.trim() || null,
        dueDays: Math.max(0, this.selectedDueDays || 0),
        dueHours: Math.max(0, this.selectedDueHours || 0),
        dueMinutes: Math.max(0, this.selectedDueMinutes || 0),
        metadataJson: this.selectedMetadataJson.trim() || '{}',
        configurationJson: this.selectedConfigJson.trim() || '{}'
      });
      this.operators.update(items => items.map(item => item.id === operator.id ? updatedOperator : item));
      return;
    }

    if (this.isGatewayElementType(this.selectedElementType)) {
      const gateway = this.ensureGatewayForElement(elementId, this.selectedElementName);
      if (!gateway) return;

      const updatedGateway: OrchestratorGateway = {
        ...gateway,
        name: this.selectedElementName.trim() || gateway.name,
        description: this.selectedElementDescription.trim(),
        type: this.selectedGatewayType,
        defaultFlowId: this.selectedGatewayDefaultFlowId || null,
        metadataJson: this.selectedGatewayMetadataJson.trim() || '{}',
        configurationJson: this.selectedConfigJson.trim() || '{}'
      };
      this.gateways.update(items => items.map(item => item.id === gateway.id ? updatedGateway : item));
      return;
    }

    if (this.isSequenceFlowElementType(this.selectedElementType) && this.bpmnModeler) {
      const elementRegistry = this.bpmnModeler.get('elementRegistry');
      const connection = elementRegistry?.get?.(elementId);
      const flow = this.ensureFlowForElement(connection);
      if (!flow) return;

      const updatedFlow: OrchestratorFlow = {
        ...flow,
        name: this.selectedElementName.trim() || flow.name,
        description: this.selectedElementDescription.trim(),
        conditionType: this.selectedFlowConditionType,
        conditionExpression: this.selectedFlowConditionExpression,
        isDefault: this.selectedFlowIsDefault,
        configurationJson: this.selectedConfigJson.trim() || '{}'
      };
      this.flows.update(items => items.map(item => item.id === flow.id ? updatedFlow : item));
    }
  }

  private loadOrchestratorSnapshot(raw: string): void {
    try {
      const snapshot = JSON.parse(raw || '{}') as Partial<OrchestratorDesignSnapshot>;
      this.startTriggers.set((snapshot.startTriggers ?? []).map(item => ({
        id: item.id || crypto.randomUUID(),
        name: item.name ?? '',
        type: item.type ?? 'Manual',
        configurationJson: item.configurationJson ?? '{}'
      })));
      this.operators.set((snapshot.operators ?? []).map(item => ({
        id: item.id || crypto.randomUUID(),
        name: item.name ?? '',
        type: item.type ?? 'UserTask',
        bpmnElementId: item.bpmnElementId ?? '',
        description: item.description ?? '',
        referenceType: item.referenceType ?? 'none',
        referenceId: item.referenceId ?? null,
        assigneeType: item.assigneeType ?? null,
        assigneeId: item.assigneeId ?? null,
        dueDays: item.dueDays ?? 0,
        dueHours: item.dueHours ?? 0,
        dueMinutes: item.dueMinutes ?? 0,
        metadataJson: item.metadataJson ?? '{}',
        configurationJson: item.configurationJson ?? '{}'
      })));
      this.gateways.set((snapshot.gateways ?? []).map(item => ({
        id: item.id || crypto.randomUUID(),
        name: item.name ?? '',
        type: item.type ?? 'ExclusiveGateway',
        bpmnElementId: item.bpmnElementId ?? '',
        description: item.description ?? '',
        defaultFlowId: item.defaultFlowId ?? null,
        metadataJson: item.metadataJson ?? '{}',
        configurationJson: item.configurationJson ?? '{}'
      })));
      this.flows.set((snapshot.flows ?? []).map(item => ({
        id: item.id || crypto.randomUUID(),
        name: item.name ?? '',
        bpmnElementId: item.bpmnElementId ?? '',
        sourceElementId: item.sourceElementId ?? '',
        targetElementId: item.targetElementId ?? '',
        description: item.description ?? '',
        conditionType: item.conditionType ?? 'Expression',
        conditionExpression: item.conditionExpression ?? '',
        isDefault: item.isDefault ?? false,
        configurationJson: item.configurationJson ?? '{}'
      })));
    } catch {
      this.startTriggers.set([this.createDefaultTrigger()]);
      this.operators.set([this.createDefaultOperator()]);
      this.gateways.set([]);
      this.flows.set([]);
    }

    if (this.startTriggers().length === 0) {
      this.startTriggers.set([this.createDefaultTrigger()]);
    }

    if (this.operators().length === 0) {
      this.operators.set([this.createDefaultOperator()]);
    }
  }

  private createDefaultTrigger(): OrchestratorStartTrigger {
    return {
      id: DEFAULT_TRIGGER.id,
      name: DEFAULT_TRIGGER.name,
      type: DEFAULT_TRIGGER.type,
      configurationJson: DEFAULT_TRIGGER.configurationJson
    };
  }

  private createDefaultOperator(): OrchestratorOperator {
    return {
      id: DEFAULT_OPERATOR.id,
      name: DEFAULT_OPERATOR.name,
      type: DEFAULT_OPERATOR.type,
      bpmnElementId: DEFAULT_OPERATOR.bpmnElementId,
      description: '',
      referenceType: 'none',
      referenceId: null,
      assigneeType: null,
      assigneeId: null,
      dueDays: 0,
      dueHours: 0,
      dueMinutes: 0,
      metadataJson: '{}',
      configurationJson: DEFAULT_OPERATOR.configurationJson
    };
  }

  private buildParsedGraphSnapshot(): string {
    const snapshot: OrchestratorDesignSnapshot = {
      designVersion: 1,
      bpmnXmlHash: `${this.bpmnXml.length}`,
      startTriggers: this.startTriggers(),
      operators: this.operators().map(operator => this.finalizeOperatorSnapshot(operator)),
      gateways: this.gateways(),
      flows: this.flows()
    };
    return JSON.stringify(snapshot);
  }

  private finalizeOperatorSnapshot(operator: OrchestratorOperator): OrchestratorOperator {
    return {
      ...operator,
      configurationJson: finalizeOperatorConfiguration(operator)
    };
  }

  private syncArtifactsFromDiagram(): void {
    if (!this.bpmnModeler) return;
    const elementRegistry = this.bpmnModeler.get('elementRegistry');
    const allElements = elementRegistry?.getAll?.() as any[] | undefined;
    if (!allElements?.length) return;

    const taskIds = new Set<string>();
    const gatewayIds = new Set<string>();
    const flowIds = new Set<string>();

    for (const element of allElements) {
      const businessObject = element?.businessObject;
      const id = businessObject?.id ? `${businessObject.id}` : '';
      const type = businessObject?.$type ? `${businessObject.$type}` : '';
      if (!id || !type) continue;

      if (this.isTaskElementType(type)) {
        taskIds.add(id);
        this.ensureOperatorForElement(id, `${businessObject?.name ?? ''}`);
        continue;
      }

      if (this.isGatewayElementType(type)) {
        gatewayIds.add(id);
        this.ensureGatewayForElement(id, `${businessObject?.name ?? ''}`);
        continue;
      }

      if (this.isSequenceFlowElementType(type)) {
        flowIds.add(id);
        this.ensureFlowForElement(element);
      }
    }

    this.operators.update(items => items.filter(item => taskIds.has(item.bpmnElementId)));
    this.gateways.update(items => items.filter(item => gatewayIds.has(item.bpmnElementId)));
    this.flows.update(items => items.filter(item => flowIds.has(item.bpmnElementId)));
  }

  private applyVersionToDesigner(version: ProcessVersionDetail): void {
    this.bpmnXml = version.bpmnXml || this.bpmnXml;
    this.versionNotes = version.notes || '';
    this.loadOrchestratorSnapshot(version.parsedGraphSnapshot || '{}');
    this.storeDesignBaseline();
    this.selectedCanonicalGraphVersionId = version.id;
    if (this.activeDetailTab() === 'canonical') {
      this.loadCanonicalGraph();
    }
    void this.renderDiagram();
    this.messages.add({
      severity: 'success',
      summary: this.t('workflow.versions.loaded').replace('{version}', `${version.version}`)
    });
  }

  private resolveInitialCanonicalGraphVersionId(definition: ProcessDefinitionDetail): string {
    if (definition.versions.length === 0) {
      return '';
    }

    const byPriority = [...definition.versions].sort((left, right) => {
      if (left.isDefault && !right.isDefault) return -1;
      if (!left.isDefault && right.isDefault) return 1;
      return right.version - left.version;
    });
    return byPriority[0]?.id ?? '';
  }

  private buildVersionLoadWarningMessage(version: ProcessVersionDetail): string {
    const current = this.extractBpmnStats(this.bpmnXml);
    const target = this.extractBpmnStats(version.bpmnXml);

    let message = this.t('workflow.versions.unsavedChangesWarning');
    message += '\n\n';
    message += this.t('workflow.versions.compareLine')
      .replace('{label}', this.t('workflow.versions.metric.tasks'))
      .replace('{current}', `${current.tasks}`)
      .replace('{target}', `${target.tasks}`);
    message += '\n';
    message += this.t('workflow.versions.compareLine')
      .replace('{label}', this.t('workflow.versions.metric.gateways'))
      .replace('{current}', `${current.gateways}`)
      .replace('{target}', `${target.gateways}`);
    message += '\n';
    message += this.t('workflow.versions.compareLine')
      .replace('{label}', this.t('workflow.versions.metric.flows'))
      .replace('{current}', `${current.sequenceFlows}`)
      .replace('{target}', `${target.sequenceFlows}`);
    message += '\n';
    message += this.t('workflow.versions.compareLine')
      .replace('{label}', this.t('workflow.versions.metric.startEvents'))
      .replace('{current}', `${current.startEvents}`)
      .replace('{target}', `${target.startEvents}`);
    message += '\n';
    message += this.t('workflow.versions.compareLine')
      .replace('{label}', this.t('workflow.versions.metric.endEvents'))
      .replace('{current}', `${current.endEvents}`)
      .replace('{target}', `${target.endEvents}`);

    message += '\n\n';
    message += this.t('workflow.versions.loadConfirm');
    return message;
  }

  private validateCurrentDefinition(): string[] {
    const errors: string[] = [];
    if (!this.definitionName.trim()) {
      errors.push(this.t('workflow.validation.nameRequired'));
    }
    if (!this.bpmnXml.trim()) {
      errors.push(this.t('workflow.validation.bpmnRequired'));
      return errors;
    }

    let parsedXml: Document;
    try {
      parsedXml = new DOMParser().parseFromString(this.bpmnXml, 'application/xml');
    } catch {
      errors.push(this.t('workflow.validation.bpmnInvalid'));
      return errors;
    }

    if (parsedXml.querySelector('parsererror')) {
      errors.push(this.t('workflow.validation.bpmnInvalid'));
      return errors;
    }

    const allElements = Array.from(parsedXml.getElementsByTagName('*'));
    const elementIds = new Set(
      allElements
        .map(element => element.getAttribute('id') ?? '')
        .filter(value => value.trim().length > 0)
    );
    if (elementIds.size === 0) {
      errors.push(this.t('workflow.validation.elementsRequired'));
      return errors;
    }

    const startEvents = allElements.filter(element => this.xmlLocalName(element) === 'startEvent');
    const endEvents = allElements.filter(element => this.xmlLocalName(element) === 'endEvent');
    if (startEvents.length === 0) errors.push(this.t('workflow.validation.startEventRequired'));
    if (endEvents.length === 0) errors.push(this.t('workflow.validation.endEventRequired'));

    const taskLikeElements = allElements.filter(element => {
      const localName = this.xmlLocalName(element);
      return localName === 'task' || localName === 'userTask' || localName === 'serviceTask';
    });
    if (taskLikeElements.length === 0) {
      errors.push(this.t('workflow.validation.taskRequired'));
    }

    const sequenceFlows = allElements
      .filter(element => this.xmlLocalName(element) === 'sequenceFlow')
      .map(element => ({
        id: element.getAttribute('id') ?? '',
        sourceRef: element.getAttribute('sourceRef') ?? '',
        targetRef: element.getAttribute('targetRef') ?? ''
      }))
      .filter(item => item.id && item.sourceRef && item.targetRef);

    if (sequenceFlows.length === 0) {
      errors.push(this.t('workflow.validation.sequenceFlowRequired'));
    }

    for (const element of allElements) {
      const elementId = element.getAttribute('id') ?? '';
      const defaultFlowId = element.getAttribute('default') ?? '';
      if (!elementId || !defaultFlowId) continue;
      const validDefaultFlow = sequenceFlows.some(flow => flow.id === defaultFlowId && flow.sourceRef === elementId);
      if (!validDefaultFlow) {
        errors.push(this.t('workflow.validation.defaultFlowInvalid'));
        break;
      }
    }

    const startIds = startEvents
      .map(element => element.getAttribute('id') ?? '')
      .filter(value => value.trim().length > 0);
    const reachable = this.buildReachableSet(startIds, sequenceFlows);

    const operatorElementIds = this.operators()
      .map(item => item.bpmnElementId.trim())
      .filter(value => value.length > 0);
    if (operatorElementIds.length === 0) {
      errors.push(this.t('workflow.validation.operatorRequired'));
    }
    for (const operatorElementId of operatorElementIds) {
      if (!elementIds.has(operatorElementId)) {
        errors.push(this.t('workflow.validation.operatorElementMissing'));
        break;
      }
      if (!reachable.has(operatorElementId)) {
        errors.push(this.t('workflow.validation.operatorElementUnreachable'));
        break;
      }
    }

    for (const operator of this.operators()) {
      if (operator.type === 'ModuleSurface') {
        const validation = validateModuleSurfaceOperatorConfiguration(operator.configurationJson);
        if (!validation.moduleKey?.trim()) {
          errors.push(this.t('workflow.designer.moduleSurface.validation.moduleKeyRequired'));
        }
        if (!validation.surfaceKey?.trim()) {
          errors.push(this.t('workflow.designer.moduleSurface.validation.surfaceKeyRequired'));
        }
      }

      if (operator.type === 'Form') {
        const referenceId = `${operator.referenceId ?? ''}`.trim();
        if (referenceId) {
          const finalized = finalizeOperatorConfiguration(operator);
          try {
            const parsed = JSON.parse(finalized) as { formDefinitionId?: string };
            if (!parsed.formDefinitionId) {
              errors.push(this.t('workflow.designer.validation.formDefinitionRequired'));
            }
          } catch {
            errors.push(this.t('workflow.designer.validation.formDefinitionRequired'));
          }
        }
      }
    }

    return errors;
  }

  private collectDefinitionWarnings(): string[] {
    const warnings: string[] = [];

    for (const operator of this.operators()) {
      if (operator.type !== 'ModuleSurface') {
        continue;
      }

      const validation = validateModuleSurfaceOperatorConfiguration(operator.configurationJson);
      if (!validation.moduleKey?.trim() || !validation.surfaceKey?.trim()) {
        continue;
      }

      if (!this.surfaceRegistry.resolve(validation.moduleKey, validation.surfaceKey)) {
        warnings.push(
          this.t('workflow.designer.moduleSurface.unregisteredWarningNamed')
            .replace('{moduleKey}', validation.moduleKey)
            .replace('{surfaceKey}', validation.surfaceKey)
        );
      }
    }

    return warnings;
  }

  private buildReachableSet(
    startIds: string[],
    sequenceFlows: Array<{ id: string; sourceRef: string; targetRef: string }>
  ): Set<string> {
    const adjacency = new Map<string, string[]>();
    for (const flow of sequenceFlows) {
      const items = adjacency.get(flow.sourceRef) ?? [];
      items.push(flow.targetRef);
      adjacency.set(flow.sourceRef, items);
    }

    const reachable = new Set<string>();
    const queue = [...startIds];
    while (queue.length > 0) {
      const current = queue.shift();
      if (!current || reachable.has(current)) continue;
      reachable.add(current);
      for (const next of adjacency.get(current) ?? []) {
        queue.push(next);
      }
    }
    return reachable;
  }

  private xmlLocalName(element: Element): string {
    return element.localName || element.tagName.replace(/^.*:/, '');
  }

  private extractBpmnStats(xml: string): {
    startEvents: number;
    endEvents: number;
    tasks: number;
    gateways: number;
    sequenceFlows: number;
  } {
    const empty = { startEvents: 0, endEvents: 0, tasks: 0, gateways: 0, sequenceFlows: 0 };
    if (!xml?.trim()) return empty;

    try {
      const parsedXml = new DOMParser().parseFromString(xml, 'application/xml');
      if (parsedXml.querySelector('parsererror')) return empty;
      const elements = Array.from(parsedXml.getElementsByTagName('*'));
      let startEvents = 0;
      let endEvents = 0;
      let tasks = 0;
      let gateways = 0;
      let sequenceFlows = 0;
      for (const element of elements) {
        const localName = this.xmlLocalName(element);
        if (localName === 'startEvent') startEvents += 1;
        if (localName === 'endEvent') endEvents += 1;
        if (localName === 'task' || localName === 'userTask' || localName === 'serviceTask') tasks += 1;
        if (localName === 'exclusiveGateway' || localName === 'parallelGateway' || localName === 'inclusiveGateway' || localName === 'eventBasedGateway') gateways += 1;
        if (localName === 'sequenceFlow') sequenceFlows += 1;
      }
      return { startEvents, endEvents, tasks, gateways, sequenceFlows };
    } catch {
      return empty;
    }
  }

  private storeBpmnBaseline(xml: string): void {
    this.baselineBpmnXml = this.normalizeBpmnXml(xml);
  }

  private storeDesignBaseline(): void {
    this.storeBpmnBaseline(this.bpmnXml);
    this.baselineDesignSnapshot = this.canonicalizeDesignSnapshot(this.buildParsedGraphSnapshot());
    this.bpmnTextareaDirty = false;
  }

  private async prepareDesignForSave(): Promise<void> {
    if (this.bpmnModeler && !this.bpmnTextareaDirty) {
      await this.syncXmlFromDiagram(false);
      this.syncArtifactsFromDiagram();
    }
  }

  private canonicalizeDesignSnapshot(json: string): string {
    try {
      return JSON.stringify(JSON.parse(json));
    } catch {
      return json;
    }
  }

  private hasDesignChanged(): boolean {
    if (this.hasBpmnChanged(this.bpmnXml)) {
      return true;
    }

    if (!this.baselineDesignSnapshot) {
      return true;
    }

    return this.canonicalizeDesignSnapshot(this.buildParsedGraphSnapshot()) !== this.baselineDesignSnapshot;
  }

  private hasBpmnChanged(currentXml: string): boolean {
    if (!this.baselineBpmnXml) return true;
    return this.normalizeBpmnXml(currentXml) !== this.baselineBpmnXml;
  }

  private normalizeBpmnXml(xml: string): string {
    return (xml ?? '')
      .replace(/>\s+</g, '><')
      .replace(/\r\n/g, '\n')
      .replace(/\n\s+/g, '\n')
      .trim();
  }

  private resolveWorkflowApiError(error: unknown): string {
    const errorCode = (error as any)?.error?.Error as string | undefined;
    if (!errorCode) return this.t('common.saveFailed');
    const translated = this.t(`workflow.errors.${errorCode}`);
    return translated === `workflow.errors.${errorCode}` ? errorCode : translated;
  }

  private loadFormReferences(): void {
    this.dynamicApi.listForms().subscribe({
      next: forms => this.formReferences.set(forms),
      error: () => this.formReferences.set([])
    });
  }

  private bindModelerEvents(): void {
    if (!this.bpmnModeler || this.modelerEventsBound) return;
    this.debugLog('bindModelerEvents:binding');

    this.bpmnModeler.on('element.click', (event: { element?: any }) => {
      this.ngZone.run(() => {
        const element = event.element ?? null;
        this.debugLog('element.click', {
          id: element?.businessObject?.id ?? null,
          type: element?.businessObject?.$type ?? null
        });
        this.loadSelectedElement(element);
      });
    });

    this.bpmnModeler.on('element.dblclick', (event: { element?: any }) => {
      this.ngZone.run(() => {
        const element = event.element ?? null;
        this.debugLog('element.dblclick', {
          id: element?.businessObject?.id ?? null,
          type: element?.businessObject?.$type ?? null
        });
        this.loadSelectedElement(element);
      });
    });

    this.bpmnModeler.on('selection.changed', (event: { newSelection?: any[] }) => {
      this.ngZone.run(() => {
        const selected = event.newSelection?.[0] ?? null;
        this.debugLog('selection.changed', {
          size: event.newSelection?.length ?? 0,
          id: selected?.businessObject?.id ?? null,
          type: selected?.businessObject?.$type ?? null
        });
        this.loadSelectedElement(selected);
      });
    });

    this.bpmnModeler.on('commandStack.shape.create.postExecute', (event: { context?: { shape?: any } }) => {
      this.ngZone.run(() => {
        const shape = event.context?.shape;
        const type = shape?.businessObject?.$type as string | undefined;
        const id = shape?.businessObject?.id as string | undefined;
        if (!type || !id) return;
        if (this.isTaskElementType(type)) {
          this.ensureOperatorForElement(id, shape.businessObject.name || '');
        } else if (this.isGatewayElementType(type)) {
          this.ensureGatewayForElement(id, shape.businessObject.name || '');
        }
      });
    });

    this.bpmnModeler.on('commandStack.shape.delete.postExecute', (event: { context?: { shape?: any } }) => {
      this.ngZone.run(() => {
        const id = event.context?.shape?.businessObject?.id as string | undefined;
        if (!id) return;
        this.operators.update(items => items.filter(item => item.bpmnElementId !== id));
        this.gateways.update(items => items.filter(item => item.bpmnElementId !== id));
        this.flows.update(items => items.filter(item => item.bpmnElementId !== id && item.sourceElementId !== id && item.targetElementId !== id));
        if (this.selectedElementId() === id) {
          this.clearSelectedElement();
        }
      });
    });

    this.bpmnModeler.on('commandStack.connection.create.postExecute', (event: { context?: { connection?: any } }) => {
      this.ngZone.run(() => {
        const connection = event.context?.connection;
        if (!connection?.businessObject) return;
        this.ensureFlowForElement(connection);
      });
    });

    this.bpmnModeler.on('commandStack.connection.delete.postExecute', (event: { context?: { connection?: any } }) => {
      this.ngZone.run(() => {
        const flowId = event.context?.connection?.businessObject?.id as string | undefined;
        if (!flowId) return;
        this.flows.update(items => items.filter(item => item.bpmnElementId !== flowId));
        if (this.selectedElementId() === flowId) {
          this.clearSelectedElement();
        }
      });
    });

    this.bpmnModeler.on('commandStack.connection.reconnect.postExecute', (event: { context?: { connection?: any } }) => {
      this.ngZone.run(() => {
        const connection = event.context?.connection;
        if (!connection?.businessObject) return;
        this.ensureFlowForElement(connection, true);
      });
    });

    this.bpmnModeler.on('commandStack.element.updateProperties.postExecute', (event: { context?: { element?: any } }) => {
      this.ngZone.run(() => {
        if (this.isApplyingProperties) {
          return;
        }
        const element = event.context?.element;
        const id = element?.businessObject?.id as string | undefined;
        const type = element?.businessObject?.$type as string | undefined;
        if (!id || !type) return;

        if (this.isGatewayElementType(type)) {
          const name = `${element?.businessObject?.name ?? ''}`;
          this.gateways.update(items => items.map(item => item.bpmnElementId === id ? { ...item, name: name || item.name } : item));
        } else if (this.isSequenceFlowElementType(type)) {
          const existing = this.flows().find(item => item.bpmnElementId === id);
          if (existing) {
            const sourceElementId = element?.businessObject?.sourceRef?.id ? `${element.businessObject.sourceRef.id}` : existing.sourceElementId;
            const targetElementId = element?.businessObject?.targetRef?.id ? `${element.businessObject.targetRef.id}` : existing.targetElementId;
            const name = `${element?.businessObject?.name ?? ''}` || `${sourceElementId} -> ${targetElementId}`;
            this.flows.update(items => items.map(item => item.id === existing.id ? {
              ...item,
              name,
              sourceElementId,
              targetElementId
            } : item));
          }
        }

        if (id === this.selectedElementId()) {
          this.loadSelectedElement(element);
        }
      });
    });

    this.modelerEventsBound = true;
    this.debugLog('bindModelerEvents:done');
  }

  private loadSelectedElement(element: any | null): void {
    if (!element?.businessObject?.id) {
      this.debugLog('loadSelectedElement:empty');
      this.clearSelectedElement();
      return;
    }

    const businessObject = element.businessObject;
    const elementId = `${businessObject.id}`;
    const elementType = `${businessObject.$type ?? ''}`;
    const name = `${businessObject.name ?? ''}`;

    this.selectedElementId.set(elementId);
    this.selectedElementType = elementType;
    this.selectedElementName = name;
    this.selectedElementDescription = '';
    this.selectedOperatorType = 'UserTask';
    this.selectedReferenceType = 'none';
    this.selectedReferenceId = '';
    this.selectedPartyKind = null;
    this.selectedPartyId = '';
    this.selectedDueDays = 0;
    this.selectedDueHours = 0;
    this.selectedDueMinutes = 0;
    this.selectedMetadataJson = '{}';
    this.selectedConfigJson = '{}';
    this.selectedStartType = this.definitionStartType;
    this.selectedEndResultType = this.definitionResultType;
    this.selectedGatewayType = this.mapBpmnGatewayToDesignType(elementType);
    this.selectedGatewayDefaultFlowId = '';
    this.selectedGatewayMetadataJson = '{}';
    this.selectedFlowConditionType = 'Expression';
    this.selectedFlowConditionExpression = '';
    this.selectedFlowIsDefault = false;
    this.selectedFlowSourceElementId = '';
    this.selectedFlowTargetElementId = '';
    this.debugLog('loadSelectedElement:base', { elementId, elementType, name });

    if (this.isStartEventType(elementType)) {
      const firstTrigger = this.startTriggers()[0] ?? null;
      const config = this.parseOperatorConfig(firstTrigger?.configurationJson ?? '{}');
      this.selectedElementDescription = `${config['description'] ?? ''}`;
      this.selectedStartType = `${config['startType'] ?? this.definitionStartType ?? 'manual'}`;
      this.selectedConfigJson = firstTrigger?.configurationJson || '{}';
      return;
    }

    if (this.isEndEventType(elementType)) {
      const config = this.parseOperatorConfig('{}');
      this.selectedElementDescription = `${config['description'] ?? ''}`;
      this.selectedEndResultType = this.definitionResultType || 'approval';
      this.selectedConfigJson = '{}';
      return;
    }

    if (this.isGatewayElementType(elementType)) {
      const gateway = this.gateways().find(item => item.bpmnElementId === elementId)
        ?? this.ensureGatewayForElement(elementId, name);
      if (!gateway) return;

      const config = this.parseOperatorConfig(gateway.configurationJson);
      this.selectedElementDescription = `${gateway.description ?? config['description'] ?? ''}`;
      this.selectedGatewayType = `${gateway.type ?? config['gatewayType'] ?? this.mapBpmnGatewayToDesignType(elementType)}`;
      this.selectedGatewayDefaultFlowId = `${gateway.defaultFlowId ?? config['defaultFlowId'] ?? ''}`;
      this.selectedGatewayMetadataJson = gateway.metadataJson || '{}';
      this.selectedConfigJson = gateway.configurationJson || '{}';
      this.debugLog('loadSelectedElement:gateway', {
        gatewayId: gateway.id,
        gatewayType: this.selectedGatewayType,
        defaultFlowId: this.selectedGatewayDefaultFlowId
      });
      return;
    }

    if (this.isSequenceFlowElementType(elementType)) {
      const flow = this.flows().find(item => item.bpmnElementId === elementId)
        ?? this.ensureFlowForElement(element);
      if (!flow) return;

      const config = this.parseOperatorConfig(flow.configurationJson);
      this.selectedElementDescription = `${flow.description ?? config['description'] ?? ''}`;
      this.selectedFlowConditionType = `${flow.conditionType ?? config['conditionType'] ?? 'Expression'}`;
      this.selectedFlowConditionExpression = `${flow.conditionExpression ?? config['conditionExpression'] ?? ''}`;
      this.selectedFlowIsDefault = (flow.isDefault ?? config['isDefault']) === true;
      this.selectedFlowSourceElementId = flow.sourceElementId;
      this.selectedFlowTargetElementId = flow.targetElementId;
      this.selectedConfigJson = flow.configurationJson || '{}';
      this.debugLog('loadSelectedElement:flow', {
        flowId: flow.id,
        conditionType: this.selectedFlowConditionType,
        isDefault: this.selectedFlowIsDefault
      });
      return;
    }

    if (!this.isTaskElementType(elementType)) return;

    const operator = this.operators().find(item => item.bpmnElementId === elementId)
      ?? this.ensureOperatorForElement(elementId, name);
    if (!operator) return;

    const config = this.parseOperatorConfig(operator.configurationJson);
    this.selectedOperatorType = operator.type || 'UserTask';
    this.selectedElementDescription = operator.description || `${config['description'] ?? ''}`;
    const configuredReferenceType = `${operator.referenceType ?? config['referenceType'] ?? 'none'}`.toLowerCase() === 'form' ? 'form' : 'none';
    this.selectedReferenceType = this.selectedOperatorType === 'Form' ? 'form' : configuredReferenceType;
    this.selectedReferenceId = `${operator.referenceId ?? config['referenceId'] ?? config['formDefinitionId'] ?? ''}`;
    const configPartyKind = parsePartyKind(config['partyKind']);
    const assigneeTypeRaw = `${operator.assigneeType ?? config['assigneeType'] ?? ''}`.trim();
    this.selectedPartyKind = configPartyKind ?? parsePartyKind(assigneeTypeRaw);
    this.selectedPartyId = `${operator.assigneeId ?? config['partyId'] ?? config['assigneeId'] ?? ''}`;
    this.selectedDueDays = Number(operator.dueDays ?? config['dueDays'] ?? 0) || 0;
    this.selectedDueHours = Number(operator.dueHours ?? config['dueHours'] ?? 0) || 0;
    this.selectedDueMinutes = Number(operator.dueMinutes ?? config['dueMinutes'] ?? 0) || 0;
    this.selectedMetadataJson = operator.metadataJson || JSON.stringify(config['metadata'] ?? {}, null, 2);
    this.selectedConfigJson = operator.configurationJson || '{}';
    this.debugLog('loadSelectedElement:task', {
      operatorId: operator.id,
      operatorType: this.selectedOperatorType,
      referenceType: this.selectedReferenceType,
      referenceId: this.selectedReferenceId
    });
  }

  private clearSelectedElement(): void {
    this.debugLog('clearSelectedElement');
    this.selectedElementId.set('');
    this.selectedElementType = '';
    this.selectedElementName = '';
    this.selectedElementDescription = '';
    this.selectedOperatorType = 'UserTask';
    this.selectedReferenceType = 'none';
    this.selectedReferenceId = '';
    this.selectedPartyKind = null;
    this.selectedPartyId = '';
    this.selectedDueDays = 0;
    this.selectedDueHours = 0;
    this.selectedDueMinutes = 0;
    this.selectedMetadataJson = '{}';
    this.selectedConfigJson = '{}';
    this.selectedStartType = this.definitionStartType;
    this.selectedEndResultType = this.definitionResultType;
    this.selectedGatewayType = 'ExclusiveGateway';
    this.selectedGatewayDefaultFlowId = '';
    this.selectedGatewayMetadataJson = '{}';
    this.selectedFlowConditionType = 'Expression';
    this.selectedFlowConditionExpression = '';
    this.selectedFlowIsDefault = false;
    this.selectedFlowSourceElementId = '';
    this.selectedFlowTargetElementId = '';
  }

  private ensureOperatorForElement(elementId: string, suggestedName: string): OrchestratorOperator | null {
    const existing = this.operators().find(item => item.bpmnElementId === elementId);
    if (existing) return existing;

    const created: OrchestratorOperator = {
      id: crypto.randomUUID(),
      name: suggestedName || 'Task',
      type: 'UserTask',
      bpmnElementId: elementId,
      description: '',
      referenceType: 'none',
      referenceId: null,
      assigneeType: null,
      assigneeId: null,
      dueDays: 0,
      dueHours: 0,
      dueMinutes: 0,
      metadataJson: '{}',
      configurationJson: '{}'
    };
    this.operators.update(items => [...items, created]);
    return created;
  }

  private parseOperatorConfig(raw: string): Record<string, unknown> {
    try {
      return raw?.trim() ? JSON.parse(raw) as Record<string, unknown> : {};
    } catch {
      return {};
    }
  }

  private ensureGatewayForElement(elementId: string, suggestedName: string): OrchestratorGateway | null {
    const existing = this.gateways().find(item => item.bpmnElementId === elementId);
    if (existing) return existing;

    const elementType = this.selectedElementId() === elementId
      ? this.selectedElementType
      : this.resolveElementTypeById(elementId);
    const created: OrchestratorGateway = {
      id: crypto.randomUUID(),
      name: suggestedName || 'Gateway',
      type: this.mapBpmnGatewayToDesignType(elementType),
      bpmnElementId: elementId,
      description: '',
      defaultFlowId: null,
      metadataJson: '{}',
      configurationJson: '{}'
    };
    this.gateways.update(items => [...items, created]);
    return created;
  }

  private ensureFlowForElement(connection: any, overwriteExisting = false): OrchestratorFlow | null {
    const businessObject = connection?.businessObject;
    const flowId = businessObject?.id as string | undefined;
    if (!flowId) return null;

    const sourceElementId = businessObject.sourceRef?.id ? `${businessObject.sourceRef.id}` : '';
    const targetElementId = businessObject.targetRef?.id ? `${businessObject.targetRef.id}` : '';
    const flowName = `${businessObject.name ?? ''}` || `${sourceElementId} -> ${targetElementId}`;

    const existing = this.flows().find(item => item.bpmnElementId === flowId);
    if (existing && !overwriteExisting) {
      return existing;
    }

    if (existing && overwriteExisting) {
      this.flows.update(items => items.map(item => item.id === existing.id ? {
        ...item,
        name: flowName,
        sourceElementId,
        targetElementId
      } : item));
      return this.flows().find(item => item.id === existing.id) ?? null;
    }

    const created: OrchestratorFlow = {
      id: crypto.randomUUID(),
      name: flowName,
      bpmnElementId: flowId,
      sourceElementId,
      targetElementId,
      description: '',
      conditionType: 'Expression',
      conditionExpression: '',
      isDefault: false,
      configurationJson: '{}'
    };
    this.flows.update(items => [...items, created]);
    return created;
  }

  private resolveElementTypeById(elementId: string): string {
    if (!this.bpmnModeler) return '';
    const elementRegistry = this.bpmnModeler.get('elementRegistry');
    const element = elementRegistry?.get?.(elementId);
    return `${element?.businessObject?.$type ?? ''}`;
  }

  private mapBpmnGatewayToDesignType(type: string): string {
    switch (type) {
      case 'bpmn:ParallelGateway':
        return 'ParallelGateway';
      case 'bpmn:InclusiveGateway':
        return 'InclusiveGateway';
      case 'bpmn:EventBasedGateway':
        return 'EventBasedGateway';
      case 'bpmn:ExclusiveGateway':
      default:
        return 'ExclusiveGateway';
    }
  }

  private isTaskElementType(type: string): boolean {
    return type === 'bpmn:Task' || type === 'bpmn:UserTask' || type === 'bpmn:ServiceTask';
  }

  private isStartEventType(type: string): boolean {
    return type === 'bpmn:StartEvent';
  }

  private isEndEventType(type: string): boolean {
    return type === 'bpmn:EndEvent';
  }

  private isGatewayElementType(type: string): boolean {
    return type === 'bpmn:ExclusiveGateway'
      || type === 'bpmn:ParallelGateway'
      || type === 'bpmn:InclusiveGateway'
      || type === 'bpmn:EventBasedGateway';
  }

  private isSequenceFlowElementType(type: string): boolean {
    return type === 'bpmn:SequenceFlow';
  }

  private toPascalCase(value: string): string {
    const normalized = (value ?? '').trim();
    if (!normalized) return normalized;
    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
  }

  onSelectedPartyKindChanged(): void {
    this.selectedPartyId = '';
    this.onSelectedPropertiesChanged();
  }

  onSelectedOperatorTypeChanged(): void {
    this.debugLog('onSelectedOperatorTypeChanged', {
      selectedOperatorType: this.selectedOperatorType,
      selectedReferenceType: this.selectedReferenceType
    });
    if (this.selectedOperatorType === 'Form') {
      this.selectedReferenceType = 'form';
    } else if (this.selectedOperatorType === 'ModuleSurface') {
      this.selectedReferenceType = 'none';
      this.selectedReferenceId = '';
    } else if (this.selectedReferenceType === 'form' && !this.selectedReferenceId) {
      this.selectedReferenceType = 'none';
    }
    this.onSelectedPropertiesChanged();
  }

  private debugLog(event: string, payload?: unknown): void {
    if (!this.debugEvents) return;
    if (payload === undefined) {
      console.log(`[WorkflowDesigner] ${event}`);
      return;
    }
    console.log(`[WorkflowDesigner] ${event}`, payload);
  }

  private fitDiagramToViewport(): void {
    if (!this.bpmnModeler) return;

    try {
      const canvas = this.bpmnModeler.get('canvas');
      canvas?.resized?.();
      canvas?.zoom?.('fit-viewport', 'auto');
    } catch {
      // keep designer usable even if fitting fails
    }
  }
}

interface OrchestratorStartTrigger {
  id: string;
  name: string;
  type: string;
  configurationJson: string;
}

interface OrchestratorOperator {
  id: string;
  name: string;
  type: string;
  bpmnElementId: string;
  description: string;
  referenceType: string;
  referenceId: string | null;
  assigneeType: string | null;
  assigneeId: string | null;
  dueDays: number;
  dueHours: number;
  dueMinutes: number;
  metadataJson: string;
  configurationJson: string;
}

interface OrchestratorGateway {
  id: string;
  name: string;
  type: string;
  bpmnElementId: string;
  description: string;
  defaultFlowId: string | null;
  metadataJson: string;
  configurationJson: string;
}

interface OrchestratorFlow {
  id: string;
  name: string;
  bpmnElementId: string;
  sourceElementId: string;
  targetElementId: string;
  description: string;
  conditionType: string;
  conditionExpression: string;
  isDefault: boolean;
  configurationJson: string;
}

interface OrchestratorDesignSnapshot {
  designVersion: number;
  bpmnXmlHash: string;
  startTriggers: OrchestratorStartTrigger[];
  operators: OrchestratorOperator[];
  gateways: OrchestratorGateway[];
  flows: OrchestratorFlow[];
}
